import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuth } from './useAuth.ts';
import { DEFAULT_ENDPOINT_URL } from '../client.ts';
import { useGqlClient } from '../client.ts';
import { useQueryClient } from '@tanstack/react-query';
import { getUserDashboard, getLatestClients, getOpenHolidays, getOvertimeInfo, getTracking, getActiveUsers, getUserRequests, getOfftimeDays, getSickTrackings, getUserHasACard , getEvaluationUser, getCheckToken, updateTracking} from '../queries/user_dashboard.ts';
import { getUser } from '../queries/user.ts';
import type { UserDashboard, LatestClients, UserHolidays, CreateTimeTrackingResponse, CreateTimeTrackingVariables, GetEvaluationUserResponse, GetEvaluationUserVariables, CloseMonthUserVariables, SetPasswordVariables } from '../types/user_dashboard.ts';
import type { OvertimeInfo, GetOvertimeInfoResponse, GetOvertimeInfoVariables, Tracking, GetTrackingsResponse, GetTrackingsVariables, GetSickTrackingsResponse, GetSickTrackingsVariables, GetOpenHolidaysVariables, GetOpenHolidaysResponse, OpenHolidays } from '../types/timeoff.ts';
import type { ActiveUser, GetUserHasCarResponse, GetMeUserResponse } from '../types/user.ts';
import type { GetUserRequestsVariables, GetUserRequestsResponse, GetOfftimeDaysResponse, GetOfftimeDaysVariables, CreateSickTrackingVariables, CreateOvertimeTrackingResponse, CreateOvertimeTrackingVariables, CreateHolidayTrackingResponse, CreateHolidayTrackingVariables } from '../types/timeoff.ts';

interface UpdatePasswordVariables {
  old_password: string;
  new_password: string;
}

type CheckTokenResponse = {
  checkToken: boolean
}

interface EmergencyExistsResponse {
  emergencyExists: boolean;
}

interface EmergencyExistsVariables {
  userId?: string;
  date: string;
}

interface ToggleEmergencyResponse {
  toggleEmergency: boolean;
}

interface ToggleEmergencyVariables {
  userId?: string;
  date: string;
}

export async function uploadGraphQL(
  endpoint: string,
  query: string,
  variables: Record<string, any>,
  token?: string
) {
  const formData = new FormData();

  const operations = JSON.stringify({
    query,
    variables,
  });

  const map: Record<string, string[]> = {};
  let fileIndex = 0;

  Object.entries(variables).forEach(([key, value]) => {
    if (value instanceof File) {
      map[fileIndex] = [`variables.${key}`];
      formData.append(String(fileIndex), value);
      variables[key] = null; // must be null in operations
      fileIndex++;
    }
  });

  formData.append("operations", operations);
  formData.append("map", JSON.stringify(map));

  const response = await fetch(endpoint, {
    method: "POST",
    headers: token ? { Authorization: token } : undefined,
    body: formData,
  });

  return response.json();
}

import { createTimeTracking, deleteTimeTracking, createSickTracking, createOvertimeTracking, createHolidayTracking, updatePassword, closeMonthUser, setPassword, emergencyExistsQuery, toggleEmergencyMutation } from '../mutations/user_dashboard.ts';

export const useUserDashboard = (
) => {
  const gqlClient = useGqlClient();

  return useQuery<UserDashboard, Error>({
    queryKey: ['userDashboard'],
    queryFn: async () => {
      const res = await gqlClient.request<{ getUserDashboard: UserDashboard }>(getUserDashboard);
      return res.getUserDashboard;
    },
  });
};

export const useLatestClients = (
) => {
  const gqlClient = useGqlClient();

  return useQuery<LatestClients, Error>({
    queryKey: ['latestClients'],
    queryFn: async () => {
      const res = await gqlClient.request<{ getLatestClients: LatestClients }>(getLatestClients);
      return res.getLatestClients;
    },
  });
};

export const useActiveUsers = (
) => {
  const gqlClient = useGqlClient();

  return useQuery<ActiveUser[], Error>({
    queryKey: ['activeUsers'],
    queryFn: async () => {
      const res = await gqlClient.request<{ allActiveUsers: ActiveUser[] }>(getActiveUsers);
      return res.allActiveUsers;
    },
  });
};

export const useOvertimeInfo = (
  startDate?: string,
  endDate?: string
) => {
  const gqlClient = useGqlClient();

  return useQuery<OvertimeInfo>({
    queryKey: ['overtimeInfo', startDate, endDate],
    enabled: !!startDate && !!endDate,
    queryFn: async () => {
      const res = await gqlClient.request<
        GetOvertimeInfoResponse,
        GetOvertimeInfoVariables
      >(getOvertimeInfo, {
        start_date: startDate!,
        end_date: endDate!,
      });

      return res.getOvertimeInfo;
    },
  });
};

export const useTrackings = (variables: GetTrackingsVariables) => {
  const gqlClient = useGqlClient();

  return useQuery<Tracking[], Error>({
    queryKey: ['trackings', variables.date, variables.month, variables.year],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetTrackingsResponse,
        GetTrackingsVariables
      >(getTracking, variables);

      return res.getTrackings;
    },
    enabled: Boolean(
      variables.date || (variables.month && variables.year)
    ),
    staleTime: 0,          
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

export const useUserRequests = (
 variables: GetUserRequestsVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery<GetUserRequestsResponse, Error>({
    queryKey: ['user-requests', variables],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetUserRequestsResponse,
        GetUserRequestsVariables
      >(getUserRequests, variables);

      return res;
    },
  });
};

export const useOfftimeDays = (first: number, page: number) => {
  const gqlClient = useGqlClient();

  return useQuery<GetOfftimeDaysResponse, Error>({
    queryKey: ['offtime-days', first, page],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetOfftimeDaysResponse,
        GetOfftimeDaysVariables
      >(getOfftimeDays, {
        first,
        page,
      });

      return res;
    },
  });
};

export const useSickTrainings = (first: number, page: number) => {
  const gqlClient = useGqlClient();

  return useQuery<GetSickTrackingsResponse, Error>({
    queryKey: ['sick-trackings', first, page],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetSickTrackingsResponse,
        GetSickTrackingsVariables
      >(getSickTrackings, {
        first,
        page,
      });

      return res;
    },
  });
};

export const useUserHasCar = () => {
  const gqlClient = useGqlClient();

  return useQuery<boolean, Error>({
    queryKey: ['user-has-car'],
    queryFn: async () => {
      const res = await gqlClient.request<GetUserHasCarResponse>(
        getUserHasACard
      );

      return res.me.has_car;
    },
  });
};


// MUTATIONS \\


export const useCreateTimeTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateTimeTrackingVariables) => {
      const res = await gqlClient.request<
        CreateTimeTrackingResponse,
        CreateTimeTrackingVariables
      >(createTimeTracking, variables);

      return res.createTimeTracking;
    },

    // optional: refresh trackings after creating
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-trackings-by-user'] });
    },
  });
};

export const useDeleteTimeTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { id: string }) => {
      const res = await gqlClient.request<
        { deleteTimeTracking: boolean },
        { id: string }
      >(deleteTimeTracking, variables);

      return res.deleteTimeTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
    },
  });
};

export const useCreateSickTracking = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateSickTrackingVariables) => {
      const res = await uploadGraphQL(
        DEFAULT_ENDPOINT_URL,
        createSickTracking,
        variables,
        token
      );

      if (res?.errors?.length) {
        const message = res.errors[0]?.message || "Serverfehler";
        throw new Error(message);
      }

      if (!res?.data?.createSickTracking) {
        throw new Error("Serverfehler");
      }

      return res.data.createSickTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-trackings-by-user'] });
    },
  });
};

export const useCreateOvertimeTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateOvertimeTrackingVariables) => {
      const res = await gqlClient.request<
        CreateOvertimeTrackingResponse,
        CreateOvertimeTrackingVariables
      >(createOvertimeTracking, variables);

      return res.createOvertimeTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
    },
  });
};

export const useCreateHolidayTracking = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: CreateHolidayTrackingVariables) => {
      const res = await gqlClient.request<
        CreateHolidayTrackingResponse,
        CreateHolidayTrackingVariables
      >(createHolidayTracking, variables);

      return res.createHolidayTracking;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
      queryClient.invalidateQueries({ queryKey: ['user-trackings-by-user'] });
    },
  });
};

export const useEvaluationUser = (
  variables: GetEvaluationUserVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['evaluation-user', variables.month, variables.year],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetEvaluationUserResponse,
        GetEvaluationUserVariables
      >(getEvaluationUser, variables);

      return res.getEvaluationUser;
    },
    enabled: !!variables.month && !!variables.year,
  });
};

export const useUpdatePassword = () => {
  const gqlClient = useGqlClient();

  return useMutation({
    mutationFn: async (variables: UpdatePasswordVariables) => {
      const res = await gqlClient.request<
        {
          updatePassword: {
            id: string;
            token: string;
          };
        },
        UpdatePasswordVariables
      >(updatePassword, variables);

      return res.updatePassword;
    },
  });
};

export const useCloseMonthUser = () => {
  const queryClient = useQueryClient();
  const gqlClient = useGqlClient();

  return useMutation({
    mutationFn: async (variables: CloseMonthUserVariables) => {
      const res = await gqlClient.request<
        { closeMonthUser: { id: string } },
        CloseMonthUserVariables
      >(closeMonthUser, variables);

      return res.closeMonthUser;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluation-user'] });
      queryClient.invalidateQueries({ queryKey: ['close-month-hours'] });
      queryClient.invalidateQueries({ queryKey: ['user-evaluation-admin'] });
    },
  });
};

export function useSetPassword() {
  const gqlClient = useGqlClient();
  return useMutation({
    mutationFn: async (variables: SetPasswordVariables) => {
      return gqlClient.request(setPassword, variables);
    },
  });
};

export const useOpenHolidays = (
  variables?: GetOpenHolidaysVariables
) => {
  const gqlClient = useGqlClient();

  return useQuery<OpenHolidays, Error>({
    queryKey: ['open-holidays', variables],
    queryFn: async () => {
      const res = await gqlClient.request<
        GetOpenHolidaysResponse,
        GetOpenHolidaysVariables
      >(getOpenHolidays, variables ?? {});

      return res.getOpenHolidays;
    },
  });
};

type UseCheckTokenOptions = {
  enabled?: boolean;
};

export const useCheckToken = ({ enabled = true }: UseCheckTokenOptions = {}) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['check-token'],
    queryFn: async () => {
      const data = await gqlClient.request<CheckTokenResponse>(
        getCheckToken
      );
      return data.checkToken;
    },
    enabled,
    retry: false,
  });
};

export const useUser = () => {
  const gqlClient = useGqlClient()

  return useQuery({
    queryKey: ["user"],
    queryFn: async () => {
      const data = await gqlClient.request<GetMeUserResponse>(getUser)
      return data.me
    },
  })
};

export const useEmergencyExists = (userId: string, date: any) => {
  const gqlClient = useGqlClient();

  return useQuery({
    queryKey: ['emergency-exists', userId, date],
    queryFn: async () => {
      const res = await gqlClient.request<
        EmergencyExistsResponse,
        EmergencyExistsVariables
      >(emergencyExistsQuery, { userId, date });

      return res.emergencyExists;
    },
    enabled: Boolean(userId && date),
    staleTime: 0,
    refetchOnMount: 'always',
  });
};

export const useToggleEmergency = () => {
  const gqlClient = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { userId?: string; date: string }) => {
      const res = await gqlClient.request<
       ToggleEmergencyResponse,
       ToggleEmergencyVariables
      >(toggleEmergencyMutation, variables);

      return res.toggleEmergency;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['emergency-exists', variables.userId, variables.date] });
      queryClient.invalidateQueries({ queryKey: ['trackings'] });
      queryClient.invalidateQueries({ queryKey: ['user-trackings-by-user'] });
    },
  });
};

export const useUpdateTracking = () => {
  const client = useGqlClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: {
      id: string;
      type: 'HOLIDAY' | 'OVERTIME';
      start_date: string;
      end_date: string;
      substitute_id?: string | null;
      comment?: string | null;
      reason?: string | null;
    }) => client.request(updateTracking, variables),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-requests'] });
      queryClient.invalidateQueries({ queryKey: ['admin-requests'] });
    },
  });
};